import {
  APIProvider,
  Map,
  useMap,
  Marker,
  InfoWindow,
  useMapsLibrary
} from '@vis.gl/react-google-maps';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { customMap } from './customMap';
import styles from './styles.module.css';
import CardsView from '../CardsView';
import Labels from '../Form/Labels';
import LocationCard from '../LocationCard';
import { HeaderIconAndTitle } from '../SearchHeader';
import UpDownSortIcon from '@/assets/icons/arrow-up-down.svg?react';
import PowerIconUrl from '@/assets/icons/power.svg?url';
import { Button } from '@/components/Form/Button';
import { PowerSite } from '@/hooks/services/Locations';
import { GOOGLE_MAPS_API_KEY } from '@/mapConfig';

const sortLabels = {
  AZ: 'A-Z',
  MW: 'MW'
};

const usaMapCenter = { lat: 39.8283, lng: -98.5795 };

const MARKER_ICON = {
  url: PowerIconUrl,
  scaledSize: { width: 20, height: 32 }
};

interface MapViewProps {
  filteredLocations: PowerSite[];
  icon: React.ComponentType<{ className?: string }>;
}

function OnLoadMap({ locations }: { locations: PowerSite[] }) {
  const map = useMap();
  const maps = useMapsLibrary('core');
  const hasFittedBounds = useRef(false);

  useEffect(() => {
    if (!map || !maps) return;

    map.setOptions({ styles: customMap });

    if (!hasFittedBounds.current && locations.length > 0) {
      const bounds = new maps.LatLngBounds();
      locations.forEach(loc => {
        bounds.extend({
          lat: Number(loc.geoCoordinates?.latitude) || usaMapCenter.lat,
          lng: Number(loc.geoCoordinates?.longitude) || usaMapCenter.lng
        });
      });
      map.fitBounds(bounds);
      hasFittedBounds.current = true;
    } else if (!hasFittedBounds.current) {
      map.setCenter(usaMapCenter);
      map.setZoom(5);
    }
  }, [map, maps, locations]);

  return null;
}

function CustomMarkers({
  locations,
  hoveredLocation,
  onHover,
  onMarkerClick
}: {
  locations: PowerSite[];
  hoveredLocation: PowerSite | null;
  onHover: (location: PowerSite | null) => void;
  onMarkerClick: (location: PowerSite) => void;
}) {
  const maps = useMapsLibrary('core');
  const pixelOffset = maps ? new maps.Size(0, -40) : undefined;

  return (
    <>
      {locations.map((location: PowerSite) => (
        <Marker
          key={location.id}
          position={{
            lat: Number(location.geoCoordinates?.latitude),
            lng: Number(location.geoCoordinates?.longitude)
          }}
          icon={MARKER_ICON}
          onClick={() => onMarkerClick(location)}
          onMouseOver={() => onHover(location)}
          onMouseOut={() => onHover(null)}
        />
      ))}

      {hoveredLocation && pixelOffset && (
        <InfoWindow
          key={hoveredLocation.id}
          position={{
            lat: Number(hoveredLocation.geoCoordinates?.latitude),
            lng: Number(hoveredLocation.geoCoordinates?.longitude)
          }}
          options={{
            headerDisabled: true,
            disableAutoPan: true,
            pixelOffset
          }}
        >
          <div className={styles.tooltipContent}>
            <Labels color="normal" size="big" bold>
              {hoveredLocation.genTotalMW} MW
            </Labels>
            <Labels color="dark" size="big" bold>
              {hoveredLocation.name}
            </Labels>
          </div>
        </InfoWindow>
      )}
    </>
  );
}

const MapView: React.FC<MapViewProps> = ({ icon, filteredLocations }) => {
  const [mapReady, setMapReady] = useState(false);
  const [locations, setLocations] = useState<PowerSite[]>(filteredLocations);
  const [sortBy, setSortBy] = useState({ by: sortLabels.AZ, ascending: true });
  const [hoveredLocation, setHoveredLocation] = useState<PowerSite | null>(
    null
  );
  const navigate = useNavigate();

  const handleMarkerClick = (location: PowerSite) => {
    navigate(
      `/locations/${location.id}?name=${encodeURIComponent(location.name)}`
    );
  };

  useEffect(() => {
    if (sortBy.by === sortLabels.AZ) {
      sortByAZ(filteredLocations);
    } else {
      sortByMW(filteredLocations);
    }
  }, [filteredLocations, sortBy]);

  const sortByAZ = (locs: PowerSite[]) => {
    const sorted = [...locs].sort((a, b) => {
      const compare = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      return sortBy.ascending ? compare : -compare;
    });
    setLocations(sorted);
  };

  const sortByMW = (locs: PowerSite[]) => {
    const sorted = [...locs].sort((a, b) => {
      return sortBy.ascending
        ? a.genTotalMW - b.genTotalMW
        : b.genTotalMW - a.genTotalMW;
    });
    setLocations(sorted);
  };

  return (
    <div className={styles.mapWrapper}>
      <div className={styles.cardsMapContainer}>
        <div className={styles.cardsContainerHeader}>
          <HeaderIconAndTitle Icon={icon} title="Locations" />
        </div>

        <div className={styles.sortBtn}>
          <div className={styles.sortText}>SORT BY</div>
          <Button
            label={sortLabels.AZ}
            onClick={() => {
              sortByAZ(locations);
              setSortBy({ by: sortLabels.AZ, ascending: !sortBy.ascending });
            }}
            solid={false}
            size="small"
            icon={UpDownSortIcon}
            iconPosition="right"
          />
          <Button
            label={sortLabels.MW}
            onClick={() => {
              sortByMW(locations);
              setSortBy({ by: sortLabels.MW, ascending: !sortBy.ascending });
            }}
            solid={false}
            size="small"
            icon={UpDownSortIcon}
            iconPosition="right"
          />
        </div>

        <CardsView>
          {locations.map(location => (
            <LocationCard
              key={location.id}
              id={location.id}
              title={location.name}
              subtitle={
                location.city && location.state
                  ? `${location.city}, ${location.state}`
                  : location.city || location.state || 'No location'
              }
              value={location.genTotalMW}
              unit={location.unit}
            />
          ))}
        </CardsView>
      </div>

      <div className={styles.mapContainer}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <OnLoadMap locations={locations} />

          <Map
            onIdle={() => setMapReady(true)}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={{ lat: 0, lng: 0 }}
            defaultZoom={5}
            mapTypeControl={false}
          >
            {mapReady && (
              <CustomMarkers
                locations={locations}
                hoveredLocation={hoveredLocation}
                onHover={setHoveredLocation}
                onMarkerClick={handleMarkerClick}
              />
            )}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
};

export default MapView;
